import React, { useRef, useState } from 'react';
import { Form, Button, Card, Alert } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';
import emailjs from '@emailjs/browser';

  const ContactForm: React.FC = () => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const form = useRef<HTMLFormElement>(null);
  const dateRef = useRef<HTMLInputElement>(null);

  // Child's DOB — optional. Supports manual typing (dd/mm/yyyy) and a native
  // calendar picker; the visible field always shows/stores UK dd/mm/yyyy.
  const [dob, setDob] = useState('');

  // Progressively format a UK date as the user types: dd/mm/yyyy
  const formatDob = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8); // ddmmyyyy
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };
  const isoToDisplay = (iso: string) => {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
  };
  const displayToIso = (display: string) => {
    const m = display.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
  };
  // Open the native calendar, seeded with any date already typed
  const openDatePicker = () => {
    const el = dateRef.current as (HTMLInputElement & { showPicker?: () => void }) | null;
    if (!el) return;
    el.value = displayToIso(dob);
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); return; } catch (_) { /* fall through */ }
    }
    el.focus();
    el.click();
  };

  const [formStatus, setFormStatus] = useState({ status: '', message: '' });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormStatus({ status: '', message: '' }); // Reset status on new submission
    const token = recaptchaRef.current?.getValue();

    if (!token) {
      setFormStatus({ status: 'error', message: 'Please verify you are not a robot.' });
      return;
    }

    if (!form.current) {
      return;
    }

    emailjs.sendForm(
      'service_iu62epl',
      'template_yyfdy8h',
      form.current,
      'yMRBQgoyya8w_YTI-'
    ).then((result) => {
        setFormStatus({ status: 'success', message: 'Thank you! Your message has been sent.' });
        form.current?.reset();
        setDob('');
        recaptchaRef.current?.reset();
    }, (error) => {
        setFormStatus({ status: 'error', message: 'Failed to send message, please try again.' });
    });
  };

  return (
    <Card className="h-60 info-panel">
          <Card.Body>
            <Card.Title>CONTACT US</Card.Title>
            <Card.Text>Thinking of joining us? Get in contact and we'll respond as soon as possible.</Card.Text>
            <Form ref={form} onSubmit={handleSubmit}>
                <Form.Floating className="mb-3">
                    <Form.Control id="floatingName" name="name" type="text" placeholder="Name" className="contactus-form-list-item" required />
                    <label htmlFor="floatingName">Name</label>
                </Form.Floating>
                <Form.Floating className="mb-3">
                    <Form.Control id="floatingEmail" name="email" type="email" placeholder="name@example.com" required />
                    <label htmlFor="floatingEmail">Email address</label>
                </Form.Floating>
                <Form.Floating className="mb-3">
                    <Form.Control id="floatingPhone" name="phone" type="tel" placeholder="Phone (Optional)" />
                    <label htmlFor="floatingPhone">Phone (Optional)</label>
                </Form.Floating>
                <Form.Floating className="mb-3">
                    <Form.Control id="floatingMessage" name="message" as="textarea" placeholder="Leave a message here" style={{ height: '100px' }} required />
                    <label htmlFor="floatingMessage">Message</label>
                </Form.Floating>
                <Form.Floating className="mb-3 dob-floating">
                    <Form.Control
                    id="floatingDob"
                    name="dob"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={10}
                    placeholder="dd/mm/yyyy"
                    pattern="\d{2}/\d{2}/\d{4}"
                    title="Please use the format dd/mm/yyyy"
                    value={dob}
                    onChange={(e) => setDob(formatDob(e.target.value))}
                    />
                    <label htmlFor="floatingDob">Child's Date of Birth (Optional)</label>
                    <button
                      type="button"
                      className="dob-calendar-btn"
                      onClick={openDatePicker}
                      aria-label="Open calendar to choose a date"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </button>
                    {/* Visually-hidden native date input, used only for the calendar popup */}
                    <input
                      ref={dateRef}
                      type="date"
                      tabIndex={-1}
                      aria-hidden="true"
                      className="dob-hidden-date"
                      onChange={(e) => { if (e.target.value) setDob(isoToDisplay(e.target.value)); }}
                    />
                </Form.Floating>

                <div className="d-flex justify-content-center">
                  <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey="6LeN_s4rAAAAADSQ-2DaEE-M7JBPoYUBcKNhb_Ct"
                      theme="dark"
                      className="mb-3"
                  />
                </div>

                {/* Conditionally render the status message */}
                {formStatus.message && (
                    <Alert variant={formStatus.status === 'success' ? 'success' : 'danger'} className="mt-3">
                    {formStatus.message}
                    </Alert>
                )}

                <div className="d-flex justify-content-center">
                  <Button variant="outline-light" type="submit">
                      Send Message
                  </Button>
                </div>
            </Form>
        </Card.Body>
    </Card>
        
  );
};

export default ContactForm;